import {
  constructUrl,
  getCanonicalPathItemPath,
  normalizePathItemType
} from "@pathwright/features/src/modules/utils/urls"

// These function are used by route `onEnter` handlers to map route state to library/resource/path store state

const getSection = (section) => {
  const defaultSection = "about"
  const previousSection = App.getStore("resource").previous("section")
  const sections = ["about", "path", "community"]

  if (!sections.includes(section)) {
    return previousSection || defaultSection
  }
  return section
}

export const loadResource = async function (nextState, replaceState /* ? */) {
  const { resourceSlug, cohortId, section } = nextState.match.params
  const resourceStore = window.App.getStore("resource")
  const result = await resourceStore.action.selectResourceBySlug(
    resourceSlug,
    cohortId,
    getSection(section)
  ).promise

  const { resource } = resourceStore.getState()

  // The resource slug in the URL may not match the resource.slug.
  // This could be due to the resource slug being incomplete
  // (may just be the resource.id).
  // NOTE: this same behavior is handled by the backend, but sometimes
  // the client uses internal links that only include the resource.id.
  // These are the cases this client-side solution addresses.
  if (
    resource &&
    resource.slug !== resourceSlug &&
    resource.id == resourceSlug &&
    replaceState
  ) {
    // For semantics, lets redirect to the <resource_slug>-<id> slug
    // when the resource slug doesn't match.
    if (resource.id == resourceSlug) {
      const pathname = location.pathname.replace(
        `/${resourceSlug}/`,
        `/${resource.slug}/`
      )
      return replaceState({
        ...location,
        pathname
      })
    }
  }

  return result
}

// The expection here is that any route needing to load the path will also be using a component
// wrapped with the PathProvider which handles loading the path. Here, we simply need to resolve a promise once the path store has the path.
const loadPath = () => {
  return new Promise((resolve, reject) => {
    const { offering } = App.getStore("resource").getState()
    const { path } = App.getStore("path").getState()
    // Ensure we aren't proceeding with a path intended for some
    // other cohort.
    if (path && path.offering_id === offering.id) {
      resolve(path)
    } else {
      App.listenToOnce(App.getStore("path"), "change:path", () =>
        resolve(App.getStore("path").getState().path)
      )
    }
  })
}

export const loadResourcePath = function (nextState, replaceState /* ? */) {
  return loadResource(nextState, replaceState).then(loadPath)
}

// A /step/ or /lesson/ URL can point at an item of a different type (or at a
// row id rather than sync source id). Redirect to the canonical path before
// selecting the item so the step renderer never receives a resource.
const redirectIfPathItemUrlMismatch = (nextState, replaceState) => {
  const { pathItemSourceId, pathItemType } = nextState.match.params
  const sourceId = parseInt(pathItemSourceId, 10)
  const expectedUrlType = normalizePathItemType(pathItemType)
  if (!sourceId || !expectedUrlType) return false

  const pathStore = window.App.getStore("path")
  const item =
    pathStore.request.findItem(sourceId) ||
    pathStore.request.getPathItemById(sourceId)
  if (!item) return false

  const itemType = normalizePathItemType(
    item.get("item_type") || item.get("type")
  )
  const itemSourceId = Number(item.get("sync_source_id"))
  if (!itemType || !itemSourceId) return false
  if (itemType === expectedUrlType && itemSourceId === sourceId) return false

  // The path load is async. Skip the replace if the user already left this URL,
  // and skip selecting the mismatched item.
  const enteredPathname = nextState.location?.pathname
  if (enteredPathname && window.location.pathname !== enteredPathname) {
    return true
  }

  const { resourceSlug, cohortId, section } = nextState.match.params
  const base = `/library/${resourceSlug}/${cohortId}/${section}/`
  let pathname = constructUrl(
    base,
    getCanonicalPathItemPath({ type: itemType, sourceId: itemSourceId })
  )

  // Keep step child routes (discussion, notes) when only the source id changes.
  if (itemType === "step" && expectedUrlType === "step") {
    const currentItemUrl = constructUrl(base, `step/${sourceId}/`)
    const remainder = (nextState.location?.pathname || "").slice(
      currentItemUrl.length
    )
    if (remainder) pathname = constructUrl(pathname, remainder)
  }

  if (typeof replaceState === "function") {
    replaceState({
      pathname,
      search: nextState.location?.search,
      hash: nextState.location?.hash
    })
  }
  return true
}

export const loadResourcePathStep = function (nextState, replaceState) {
  const { pathItemSourceId } = nextState.match.params
  return loadResourcePath(nextState, replaceState).then(() => {
    if (redirectIfPathItemUrlMismatch(nextState, replaceState)) return true
    window.App.getStore("step").action.setSelectedStepByID(pathItemSourceId)
    return false
  })
}

export const loadResourcePathLesson = function (nextState, replaceState) {
  const { pathItemSourceId } = nextState.match.params
  return loadResourcePath(nextState, replaceState).then(() => {
    if (redirectIfPathItemUrlMismatch(nextState, replaceState)) return true
    window.App.getStore("path").action.selectGroupByID(pathItemSourceId)
    return false
  })
}
